/**
 * HTTP contract between clients (game, help mode) and apps/api, version /v1.
 * Both sides import these schemas; the server validates every body with them.
 */
import { z } from 'zod';
import { RoundSubmissionSchema } from './round.js';

export const BoosterKey = z.enum(['undo', 'shuffle', 'magnet', 'extraSlot']);
export type BoosterKey = z.infer<typeof BoosterKey>;

export const GuestAuthRequest = z.object({
  deviceId: z.string().min(8).max(128),
  platform: z.enum(['web', 'ios', 'android', 'discord']),
  locale: z.string().max(16).default('en'),
  birthYear: z.number().int().min(1900).max(2100).optional(),
  ref: z.string().max(64).optional(), // attribution from a shared link (SO-05)
  campaign: z.string().max(64).optional(),
  // install source, in Play install-referrer form (utm_source=…&utm_medium=…&utm_campaign=…&gclid=…): Play's referrer on
  // Android's first launch, the landing page's utm_* query on the web; the server parses it at guest creation
  installReferrer: z.string().max(1024).optional(),
});
export const RefreshRequest = z.object({ refreshToken: z.string().min(20).max(200) });
export const AuthResponse = z.object({
  playerId: z.uuid(),
  accessToken: z.string(),
  refreshToken: z.string(),
  expiresIn: z.number().int(), // seconds
});
export type AuthResponse = z.infer<typeof AuthResponse>;

export const Balances = z.object({ coins: z.number().int(), gems: z.number().int(), stars: z.number().int() });
export const Inventory = z.record(BoosterKey, z.number().int());

export const Snapshot = z.object({
  player: z.object({
    id: z.uuid(),
    shopName: z.string().nullable(),
    level: z.number().int(),
    xp: z.number().int(),
    createdAt: z.string(),
  }),
  balances: Balances,
  inventory: Inventory,
  progress: z.object({
    levels: z.record(z.string(), z.object({ bestStars: z.number().int(), bestTaps: z.number().int(), plays: z.number().int() })),
    /** `decor` (spot → decor id) only on shops that have decor placed; absent from servers before migration 0011 */
    shops: z.record(z.string(), z.object({ upgrades: z.record(z.string(), z.enum(['a', 'b', 'c'])), decor: z.record(z.string(), z.string()).optional() })),
    freeRescueUsed: z.boolean(),
  }),
  serverTime: z.number().int(),
});
export type Snapshot = z.infer<typeof Snapshot>;

export const RoundRequest = RoundSubmissionSchema;
export const RoundResponse = z.object({
  status: z.enum(['accepted', 'rejected', 'duplicate']),
  reason: z.string().optional(),
  rewards: z.object({ coins: z.number().int(), xp: z.number().int(), starsDelta: z.number().int() }),
  snapshot: Snapshot,
  /** reason `level_outdated`: the version the server plays now (refresh the city pack, then play it again) */
  currentLevel: z.object({ levelId: z.string(), levelHash: z.string(), packVersion: z.number().int().nullable() }).optional(),
});
export type RoundResponse = z.infer<typeof RoundResponse>;

export const UpgradeRequest = z.object({
  slot: z.string().regex(/^upg\d{2}$/),
  style: z.enum(['a', 'b', 'c']),
  idemKey: z.string().min(8).max(64),
});
export const ShopNameRequest = z.object({ name: z.string().min(2).max(24) });

export const ConfigResponse = z.object({
  remote: z.record(z.string(), z.unknown()),
  economyVersion: z.number().int(),
  levelPacks: z.record(z.string(), z.number().int()),
});

export const ApiError = z.object({ error: z.string(), message: z.string().optional() });
